// Structural shape so both the generated `UserPlate` and Home's local interface fit.
export interface PlateLike {
  id: number;
  vrm: string;
}

export type PlateOption =
  | { kind: 'typed'; vrm: string }
  | { kind: 'saved'; vrm: string; id: number };

export const plateEquals = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Decides what the active plate and the `selectedVrm` sentinel should be for a
 * plate the user entered. If it matches one they already have stored we select
 * that stored entry (using its canonical casing); otherwise it stays a freely
 * typed plate, flagged by the 'new' sentinel.
 */
export const resolvePlateSelection = (
  entered: string,
  plates: PlateLike[],
): { vrm: string; selectedVrm: string } => {
  const next = entered.trim();
  const match = plates.find((plate) => plateEquals(plate.vrm, next));
  return {
    vrm: match ? match.vrm : next,
    selectedVrm: match ? match.vrm : 'new',
  };
};

/**
 * The list the plate arrows step through: the user's typed plate first (when it
 * is not already one of their stored plates), then every stored plate. Derived
 * from state on every render so there is no index to keep in sync.
 */
export const buildPlateOptions = (
  typedVrm: string,
  plates: PlateLike[],
): PlateOption[] => {
  const typed = typedVrm.trim();
  // Collapse duplicates: the API can return the same plate more than once, and
  // each extra copy would otherwise show up as another step in the arrows.
  const seen = new Set<string>();
  const saved: PlateOption[] = [];
  plates.forEach((plate) => {
    const key = plate.vrm.trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    saved.push({ kind: 'saved', vrm: plate.vrm, id: plate.id });
  });
  const typedIsStored = typed !== '' && seen.has(typed.toLowerCase());
  return [
    ...(typed && !typedIsStored
      ? [{ kind: 'typed' as const, vrm: typed }]
      : []),
    ...saved,
  ];
};
